// todo: shadcn calendar, and tess picker? then use react aria for better field manual input
// also exclude segmetns from tab order
// use field group with separate button (maybe tabIndex=-1)

import { eachDayOfInterval, isSameDay } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { DayPicker } from 'react-day-picker';
import { enGB, enUS, pl, zhCN } from 'react-day-picker/locale';
import { useTranslation } from 'react-i18next';
import { cn } from 'v5/platform/dom/cn';
import { buttonVariants } from './Button';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function isDateRangeInList(startDate: Date, endDate: Date, dateList: Date[]) {
  let datesInRange = eachDayOfInterval({ start: startDate, end: endDate });

  return datesInRange.some(rangeDate =>
    dateList.some(listDate => isSameDay(rangeDate, listDate))
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  const { i18n } = useTranslation();
  return (
    <DayPicker
      locale={
        {
          'en-GB': enGB,
          en: enUS,
          zh: zhCN,
          pl: pl
        }[i18n.language as 'en-GB' | 'en' | 'zh' | 'pl'] ?? enUS
      }
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        month: 'space-y-4',
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-y-0 relative',
        month_caption: 'flex justify-center pt-1 relative items-center',
        month_grid: 'w-full border-collapse space-y-1',
        caption_label: 'text-sm font-medium',
        nav: 'flex items-center justify-between absolute inset-x-0',
        button_previous: cn(
          buttonVariants({ variant: 'secondary' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 not-aria-disabled:hover:opacity-100 z-10 disabled:!cursor-not-allowed disabled:hover:opacity-50 aria-disabled:!cursor-not-allowed aria-disabled:hover:opacity-50'
        ),
        button_next: cn(
          buttonVariants({ variant: 'secondary' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 not-aria-disabled:hover:opacity-100 z-10 disabled:!cursor-not-allowed disabled:hover:opacity-50 aria-disabled:!cursor-not-allowed aria-disabled:hover:opacity-50'
        ),
        weeks: 'w-full border-collapse space-y-',
        weekdays: 'flex',
        weekday:
          'text-gray-500 rounded-md w-9 font-normal text-[0.8rem] text-center',
        week: 'flex w-full mt-2',
        day_button:
          'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-outside)]:bg-brand-100 [&:has([aria-selected])]:bg-brand-200  focus-within:relative focus-within:z-20 disabled:cursor-not-allowed',
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 p-0 font-normal aria-selected:opacity-100',
          // disabled- or outside-month selected: bound/single vs range middle (single has no .is-range-middle)
          '[&[data-disabled][data-selected]:not(.is-range-middle),&[data-outside][data-selected]:not(.is-range-middle)]:!bg-brand-500 [&[data-disabled][data-selected]:not(.is-range-middle),&[data-outside][data-selected]:not(.is-range-middle)]:!text-white [&[data-disabled][data-selected]:not(.is-range-middle),&[data-outside][data-selected]:not(.is-range-middle)]:!opacity-30 [&[data-disabled][data-selected].is-range-middle,&[data-outside][data-selected].is-range-middle]:!bg-brand-100 [&[data-disabled][data-selected].is-range-middle,&[data-outside][data-selected].is-range-middle]:!text-fg-muted [&[data-disabled][data-selected].is-range-middle,&[data-outside][data-selected].is-range-middle]:!opacity-30'
        ),

        today:
          "relative after:content-[''] after:absolute after:top-[1px] after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:pointer-events-none after:bg-red-500 aria-selected:[&:not(.is-range-middle)::after]:bg-white",
        selected:
          'bg-brand-500 text-white border border-transparent not-aria-readonly:not-aria-disabled:aria-selected:hocus:bg-brand-600',
        range_end:
          'rounded-none rounded-r-sm not-aria-readonly:not-aria-disabled:aria-selected:hocus:bg-brand-600 not-aria-readonly:not-aria-disabled:aria-selected:hocus:text-white is-range-bound is-range-end',
        range_start:
          'rounded-none rounded-l-sm not-aria-readonly:not-aria-disabled:aria-selected:hocus:bg-brand-600 not-aria-readonly:not-aria-disabled:aria-selected:hocus:text-white is-range-bound is-range-start',
        range_middle:
          'not-aria-readonly:not-aria-disabled:aria-selected:bg-brand-100 not-aria-readonly:not-aria-disabled:aria-selected:text-brand-900 not-aria-readonly:not-aria-disabled:aria-selected:hocus:bg-brand-200 not-aria-readonly:not-aria-disabled:aria-selected:hocus:text-white rounded-none is-range-middle',
        outside:
          'day-outside text-fg-muted opacity-50 not-aria-readonly:not-aria-disabled:aria-selected:bg-brand-100 not-aria-readonly:not-aria-disabled:aria-selected:text-fg-muted not-aria-readonly:not-aria-disabled:aria-selected:opacity-30',
        disabled: 'text-fg-muted opacity-50',

        hidden: 'invisible',
        ...classNames
      }}
      modifiersClassNames={{
        highlighter:
          "relative after:content-[''] after:absolute after:top-[1px] after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:pointer-events-none after:bg-brand-400 aria-selected:[&:not(.is-range-middle)::after]:bg-brand-200"
      }}
      components={{
        Chevron: ({ ...props }) =>
          props.orientation === 'left' ? (
            <ChevronLeft {...props} className="h-4 w-4" />
          ) : (
            <ChevronRight {...props} className="h-4 w-4" />
          )
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
